// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  Radio,
  Tooltip,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import React from 'react'
import { useTranslate } from 'react-admin'
import { clampRangeToData, type DeviceWearRow } from '../utils/deviceWearPeriods'
import { useDateFormatter } from '../utils/localeDate'

export type ExportRangeMode = 'devices' | 'custom'

interface ReportDeviceSelectorProps {
  rows: DeviceWearRow[]
  isLoading: boolean
  error?: unknown
  mode: ExportRangeMode
  onModeChange: (mode: ExportRangeMode) => void
  selectedIds: Array<string | number>
  onSelectedIdsChange: (ids: Array<string | number>) => void
  customStart: Date
  customEnd: Date
  onCustomRangeChange: (start: Date, end: Date) => void
  /** The pet's actual data range; custom dates are clamped to it. */
  dataRange: { startDate: Date; endDate: Date } | null
  /** Show the 7/14/30/90-day presets in the custom section (PDF dialog). */
  showPresets?: boolean
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Report range picker, device first (vic/vetpo#56): every sensor the pet has
 * worn, newest first, multi-select, with a "<3 days" label on short wears; a
 * custom date range stays available underneath, clamped to the pet's data.
 * Used by both the PDF and the CSV export so the two cannot diverge.
 */
export const ReportDeviceSelector: React.FC<ReportDeviceSelectorProps> = ({
  rows,
  isLoading,
  error,
  mode,
  onModeChange,
  selectedIds,
  onSelectedIdsChange,
  customStart,
  customEnd,
  onCustomRangeChange,
  dataRange,
  showPresets = false,
}) => {
  const translate = useTranslate()
  const fmt = useDateFormatter()
  const selected = new Set(selectedIds.map(String))

  const toggle = (id: string | number) => {
    onModeChange('devices')
    const key = String(id)
    onSelectedIdsChange(
      selected.has(key) ? selectedIds.filter((x) => String(x) !== key) : [...selectedIds, id],
    )
  }

  // Any custom control switches to custom mode, and every value it sets is
  // clamped to the pet's actual data range (a window outside it is empty).
  // While "By sensor" is the chosen mode, the custom dates are just the
  // untouched defaults — often outside this patient's data, so MUI's
  // min/max validation drew both fields in error red on a dialog the vet had
  // not touched. Show validation only when this is the active mode. (Picking
  // a date still switches to custom mode via setCustom.)
  const inactiveFieldProps = {
    fullWidth: true,
    size: 'small' as const,
    ...(mode !== 'custom' ? { error: false } : {}),
  }

  const setCustom = (start: Date, end: Date) => {
    const c = clampRangeToData(start, end, dataRange)
    onModeChange('custom')
    onCustomRangeChange(c.start, c.end)
  }
  const applyPreset = (days: number) => {
    const end = dataRange ? dataRange.endDate : new Date()
    setCustom(new Date(end.getTime() - days * DAY_MS), end)
  }

  const dataRows = rows.filter((r) => r.first && r.last)

  return (
    <Box>
      <FormControlLabel
        control={
          <Radio
            checked={mode === 'devices'}
            onChange={() => onModeChange('devices')}
            disabled={dataRows.length === 0 || !!error}
          />
        }
        label={
          <Typography fontWeight="bold">{translate('pos.export_selection.by_device')}</Typography>
        }
      />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mb: 1 }}>
        {translate('pos.export_selection.by_device_hint')}
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 4, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">
            {translate('pos.export_selection.loading_devices')}
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate('pos.export_selection.devices_error')}
        </Alert>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
          {translate('pos.export_selection.no_devices')}
        </Typography>
      ) : (
        <List dense disablePadding sx={{ ml: 2, mb: 1 }} data-testid="export-device-list">
          {rows.map((r) => {
            const hasData = !!(r.first && r.last)
            return (
              <ListItem key={String(r.id)} disableGutters sx={{ py: 0 }}>
                <FormControlLabel
                  sx={{ width: '100%', mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={mode === 'devices' && selected.has(String(r.id))}
                      onChange={() => toggle(r.id)}
                      disabled={!hasData}
                      inputProps={{ 'aria-label': r.name }}
                    />
                  }
                  label={
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                      data-testid={`export-device-row-${r.id}`}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {r.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {hasData
                          ? `${fmt(r.first as Date)} – ${fmt(r.last as Date)} · ${translate(
                              'pos.export_selection.days_worn',
                              { smart_count: r.days },
                            )}`
                          : translate('pos.export_selection.no_readings')}
                      </Typography>
                      {r.shortWear && (
                        <Tooltip title={translate('pos.export_selection.short_wear_hint')}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={translate('pos.export_selection.short_wear')}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            )
          })}
        </List>
      )}
      {mode === 'devices' && dataRows.length > 0 && selectedIds.length === 0 && (
        <Typography variant="caption" color="warning.main" sx={{ ml: 4 }}>
          {translate('pos.export_selection.select_one')}
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={<Radio checked={mode === 'custom'} onChange={() => onModeChange('custom')} />}
          label={
            <Typography fontWeight="bold">
              {translate('pos.export_selection.custom_range')}
            </Typography>
          }
        />
        <Box sx={{ ml: 4, mt: 1 }}>
          {showPresets && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
                {translate('pos.pdf.quick_presets')}:
              </Typography>
              {[7, 14, 30, 90].map((days) => (
                <Chip
                  key={days}
                  label={translate(`pos.pdf.last_${days}_days`)}
                  onClick={() => applyPreset(days)}
                  variant="outlined"
                  size="small"
                />
              ))}
              {dataRange && (
                <Chip
                  label={translate('pos.pdf.use_actual_range')}
                  onClick={() => setCustom(dataRange.startDate, dataRange.endDate)}
                  variant="outlined"
                  size="small"
                  color="primary"
                />
              )}
            </Box>
          )}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label={translate('pos.glucose.start_date')}
                  value={customStart}
                  // Only commit fully-valid dates: while a field is being typed
                  // MUI emits an Invalid Date (truthy) that would crash format().
                  onChange={(d) => d && !Number.isNaN(d.getTime()) && setCustom(d, customEnd)}
                  minDate={dataRange?.startDate}
                  maxDate={dataRange?.endDate}
                  slotProps={{ textField: inactiveFieldProps }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label={translate('pos.glucose.end_date')}
                  value={customEnd}
                  onChange={(d) => d && !Number.isNaN(d.getTime()) && setCustom(customStart, d)}
                  minDate={customStart}
                  maxDate={dataRange?.endDate}
                  slotProps={{ textField: inactiveFieldProps }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
          {dataRange && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {translate('pos.export_selection.clamped_hint', {
                start: fmt(dataRange.startDate),
                end: fmt(dataRange.endDate),
              })}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}
